// Procedural, original placeholder models. All geometry authored in code.
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { BaseEnemyKind, EnemyKind } from '../core/content';
import { MAP_FOE_DEFS } from '../core/mapFoeDefs';
import { addOutlines, RenderStyle, toonMaterial } from './toon';

function tx(g: THREE.BufferGeometry, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0, s: number | [number, number, number] = 1): THREE.BufferGeometry {
  const m = new THREE.Matrix4();
  const sc = Array.isArray(s) ? new THREE.Vector3(...s) : new THREE.Vector3(s, s, s);
  m.compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)), sc);
  return g.applyMatrix4(m);
}

function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const clean = parts.map((g) => {
    const n = g.index ? g.toNonIndexed() : g;
    for (const k of Object.keys(n.attributes)) if (k !== 'position' && k !== 'normal') n.deleteAttribute(k);
    return n;
  });
  const out = mergeGeometries(clean, false)!;
  out.computeBoundingSphere();
  return out;
}

/** Body + emissive "eye/core" geometry per enemy kind. Origin at body centre, facing +Z. */
export function enemyGeometry(kind: EnemyKind): { body: THREE.BufferGeometry; glow: THREE.BufferGeometry } {
  if (kind in MAP_FOE_DEFS) return enemyGeometry('drone'); // MAP FOES have no classic model (enemy art only)
  switch (kind as BaseEnemyKind) {
    case 'drone': {
      const body = merge([
        new THREE.IcosahedronGeometry(0.55, 0),
        tx(new THREE.ConeGeometry(0.16, 0.6, 5), 0, 0.55, -0.1, -0.4),
        tx(new THREE.ConeGeometry(0.14, 0.5, 5), 0.45, 0.2, -0.2, -0.3, 0, -1.0),
        tx(new THREE.ConeGeometry(0.14, 0.5, 5), -0.45, 0.2, -0.2, -0.3, 0, 1.0),
        tx(new THREE.ConeGeometry(0.2, 0.7, 5), 0, -0.1, -0.6, -Math.PI / 2),
      ]);
      const glow = merge([tx(new THREE.SphereGeometry(0.11, 8, 6), 0.18, 0.1, 0.45), tx(new THREE.SphereGeometry(0.11, 8, 6), -0.18, 0.1, 0.45)]);
      return { body, glow };
    }
    case 'skitter': {
      const parts = [tx(new THREE.SphereGeometry(0.4, 8, 6), 0, 0, 0, 0, 0, 0, [1, 0.6, 1.3])];
      for (let i = 0; i < 3; i++) for (const s of [-1, 1]) parts.push(tx(new THREE.ConeGeometry(0.06, 0.7, 4), s * 0.42, -0.15, -0.25 + i * 0.25, 0, 0, s * 2.1));
      parts.push(tx(new THREE.ConeGeometry(0.1, 0.45, 4), 0, 0.05, 0.6, Math.PI / 2));
      return { body: merge(parts), glow: merge([tx(new THREE.SphereGeometry(0.08, 6, 4), 0.14, 0.12, 0.42), tx(new THREE.SphereGeometry(0.08, 6, 4), -0.14, 0.12, 0.42)]) };
    }
    case 'brute': {
      const body = merge([
        tx(new THREE.BoxGeometry(1.4, 1.3, 1.0), 0, 0.1, 0),
        tx(new THREE.BoxGeometry(0.6, 0.6, 0.6), 0.95, 0.5, 0, 0, 0, 0.3),
        tx(new THREE.BoxGeometry(0.6, 0.6, 0.6), -0.95, 0.5, 0, 0, 0, -0.3),
        tx(new THREE.BoxGeometry(0.42, 1.1, 0.42), 1.05, -0.35, 0.15),
        tx(new THREE.BoxGeometry(0.42, 1.1, 0.42), -1.05, -0.35, 0.15),
        tx(new THREE.BoxGeometry(0.6, 0.45, 0.55), 0, 0.92, 0.12),
        tx(new THREE.ConeGeometry(0.12, 0.5, 4), 0.25, 1.25, 0, 0, 0, -0.3),
        tx(new THREE.ConeGeometry(0.12, 0.5, 4), -0.25, 1.25, 0, 0, 0, 0.3),
      ]);
      const glow = merge([tx(new THREE.BoxGeometry(0.4, 0.08, 0.05), 0, 0.95, 0.4), tx(new THREE.SphereGeometry(0.2, 8, 6), 0, 0.15, 0.5)]);
      return { body, glow };
    }
    case 'caster': {
      const body = merge([
        tx(new THREE.ConeGeometry(0.55, 1.4, 6, 1, true), 0, -0.2, 0),
        tx(new THREE.SphereGeometry(0.3, 8, 6), 0, 0.65, 0),
        tx(new THREE.ConeGeometry(0.34, 0.6, 6), 0, 0.95, -0.05, -0.3),
        tx(new THREE.TorusGeometry(0.55, 0.05, 4, 16), 0, 0.2, 0, Math.PI / 2),
      ]);
      const glow = merge([tx(new THREE.OctahedronGeometry(0.22, 0), 0, 0.15, 0.7), tx(new THREE.SphereGeometry(0.07, 6, 4), 0.1, 0.68, 0.26), tx(new THREE.SphereGeometry(0.07, 6, 4), -0.1, 0.68, 0.26)]);
      return { body, glow };
    }
    case 'lancer': { // lean crystal pike-runner: long forward horn, swept fins
      const body = merge([
        tx(new THREE.CapsuleGeometry(0.32, 0.7, 4, 8), 0, 0, 0, Math.PI / 2),
        tx(new THREE.ConeGeometry(0.16, 1.1, 5), 0, 0.05, 0.95, Math.PI / 2),
        tx(new THREE.ConeGeometry(0.1, 0.6, 4), 0.3, 0.25, -0.35, -0.9, 0, -0.6),
        tx(new THREE.ConeGeometry(0.1, 0.6, 4), -0.3, 0.25, -0.35, -0.9, 0, 0.6),
        tx(new THREE.CylinderGeometry(0.05, 0.05, 0.6, 4), 0.2, -0.45, 0.2), tx(new THREE.CylinderGeometry(0.05, 0.05, 0.6, 4), -0.2, -0.45, 0.2),
        tx(new THREE.CylinderGeometry(0.05, 0.05, 0.6, 4), 0.2, -0.45, -0.3), tx(new THREE.CylinderGeometry(0.05, 0.05, 0.6, 4), -0.2, -0.45, -0.3),
      ]);
      return { body, glow: merge([tx(new THREE.BoxGeometry(0.34, 0.06, 0.06), 0, 0.18, 0.48)]) };
    }
    case 'stomper': { // squat basalt golem with huge slab fists
      const body = merge([
        tx(new THREE.DodecahedronGeometry(0.95, 0), 0, 0.2, 0, 0, 0, 0, [1.2, 0.9, 1]),
        tx(new THREE.BoxGeometry(0.75, 0.75, 0.75), 1.15, -0.25, 0.25, 0.2, 0, 0.2),
        tx(new THREE.BoxGeometry(0.75, 0.75, 0.75), -1.15, -0.25, 0.25, 0.2, 0, -0.2),
        tx(new THREE.CylinderGeometry(0.3, 0.38, 0.7, 6), 0.5, -0.85, 0), tx(new THREE.CylinderGeometry(0.3, 0.38, 0.7, 6), -0.5, -0.85, 0),
        tx(new THREE.BoxGeometry(0.55, 0.3, 0.45), 0, 1.05, 0.15),
      ]);
      return { body, glow: merge([tx(new THREE.SphereGeometry(0.12, 6, 4), 0.15, 1.08, 0.4), tx(new THREE.SphereGeometry(0.12, 6, 4), -0.15, 1.08, 0.4), tx(new THREE.TorusGeometry(0.4, 0.06, 4, 12), 0, 0.3, 0.75)]) };
    }
    case 'bomber': { // round volatile pod on little legs, glowing seams
      const body = merge([
        new THREE.SphereGeometry(0.5, 10, 8),
        tx(new THREE.CylinderGeometry(0.06, 0.06, 0.35, 5), 0, 0.6, 0),
        tx(new THREE.CylinderGeometry(0.05, 0.05, 0.4, 4), 0.25, -0.5, 0.15, 0, 0, 0.3), tx(new THREE.CylinderGeometry(0.05, 0.05, 0.4, 4), -0.25, -0.5, 0.15, 0, 0, -0.3),
        tx(new THREE.CylinderGeometry(0.05, 0.05, 0.4, 4), 0, -0.5, -0.28, 0.3),
      ]);
      return { body, glow: merge([tx(new THREE.TorusGeometry(0.5, 0.05, 4, 16), 0, 0, 0, Math.PI / 2), tx(new THREE.SphereGeometry(0.12, 6, 4), 0, 0.8, 0)]) };
    }
    case 'splitter': { // twin-lobed crystal mass along a visible fault line
      const body = merge([
        tx(new THREE.IcosahedronGeometry(0.62, 0), 0.32, 0, 0), tx(new THREE.IcosahedronGeometry(0.62, 0), -0.32, 0.05, 0, 0.4, 0.3, 0),
        tx(new THREE.ConeGeometry(0.15, 0.5, 4), 0.4, 0.6, 0, 0, 0, -0.3), tx(new THREE.ConeGeometry(0.15, 0.5, 4), -0.4, 0.6, 0, 0, 0, 0.3),
      ]);
      return { body, glow: merge([tx(new THREE.BoxGeometry(0.08, 1.1, 0.9), 0, 0, 0)]) };
    }
    case 'wisp': { // tiny winged spark
      const body = merge([
        new THREE.OctahedronGeometry(0.3, 0),
        tx(new THREE.ConeGeometry(0.22, 0.5, 3), 0.35, 0.05, -0.1, 0, 0, -1.4, [1, 1, 0.2]),
        tx(new THREE.ConeGeometry(0.22, 0.5, 3), -0.35, 0.05, -0.1, 0, 0, 1.4, [1, 1, 0.2]),
      ]);
      return { body, glow: merge([new THREE.SphereGeometry(0.16, 6, 4)]) };
    }
    case 'warden': { // tall plated sentinel with a tower-shield front
      const body = merge([
        tx(new THREE.CylinderGeometry(0.7, 0.85, 1.9, 6), 0, 0, 0),
        tx(new THREE.BoxGeometry(1.6, 1.7, 0.25), 0, 0.05, 0.85),
        tx(new THREE.ConeGeometry(0.55, 0.7, 6), 0, 1.3, 0),
        tx(new THREE.BoxGeometry(0.35, 0.9, 0.35), 0.95, 0.1, 0), tx(new THREE.BoxGeometry(0.35, 0.9, 0.35), -0.95, 0.1, 0),
      ]);
      return { body, glow: merge([tx(new THREE.BoxGeometry(0.9, 0.08, 0.05), 0, 0.55, 1.0), tx(new THREE.BoxGeometry(0.08, 0.9, 0.05), 0, 0.1, 1.0)]) };
    }
    case 'herald': // THE NULL HERALD (classic fallback look = the boss silhouette)
    case 'boss': {
      const body = merge([
        tx(new THREE.CylinderGeometry(0.9, 0.6, 2.2, 8), 0, 0, 0),
        tx(new THREE.SphereGeometry(0.85, 10, 8), 0, 1.25, 0, 0, 0, 0, [1.4, 0.8, 1]),
        tx(new THREE.SphereGeometry(0.55, 10, 8), 0, 1.85, 0.15),
        tx(new THREE.ConeGeometry(0.18, 1.1, 5), 0.45, 2.4, 0, -0.3, 0, -0.5),
        tx(new THREE.ConeGeometry(0.18, 1.1, 5), -0.45, 2.4, 0, -0.3, 0, 0.5),
        tx(new THREE.ConeGeometry(0.3, 1.0, 5), 1.25, 1.65, 0, 0, 0, -0.9),
        tx(new THREE.ConeGeometry(0.3, 1.0, 5), -1.25, 1.65, 0, 0, 0, 0.9),
        tx(new THREE.CylinderGeometry(0.28, 0.22, 1.6, 6), 1.35, 0.4, 0.2, 0.3, 0, 0.2),
        tx(new THREE.CylinderGeometry(0.28, 0.22, 1.6, 6), -1.35, 0.4, 0.2, 0.3, 0, -0.2),
        tx(new THREE.ConeGeometry(0.9, 1.4, 8, 1, true), 0, -1.6, 0, Math.PI),
      ]);
      const glow = merge([
        tx(new THREE.SphereGeometry(0.1, 6, 4), 0.2, 1.95, 0.6), tx(new THREE.SphereGeometry(0.1, 6, 4), -0.2, 1.95, 0.6),
        tx(new THREE.OctahedronGeometry(0.4, 0), 0, 0.6, 0.85),
      ]);
      return { body, glow };
    }
  }
}

/** Aren Vale (Shatterlight GDD v0.1): dark cropped hair + brass side clasp, midnight-blue asymmetric mantle,
 *  ivory ceramic forearm armour, offset floating copper ring behind the left shoulder, long split-tail scarf.
 *  Jointed limbs (shoulder→elbow, hip→knee) so the view can run a real walk/run cycle.
 *  Local units: hips at y=0, boot soles at y=-HERO_SOLE, crown ≈ +1.45. */
export const HERO_SOLE = 1.12;
export interface HeroRig {
  root: THREE.Group; body: THREE.Group; torso: THREE.Group; head: THREE.Group;
  armL: THREE.Group; armR: THREE.Group; foreL: THREE.Group; foreR: THREE.Group;
  legL: THREE.Group; legR: THREE.Group; shinL: THREE.Group; shinR: THREE.Group;
  scarf: THREE.Group[][]; mantle: THREE.Group[]; ring: THREE.Group; ringSegs: THREE.Mesh[];
  hairMat: THREE.MeshStandardNodeMaterial | THREE.MeshToonNodeMaterial; trimMat: THREE.MeshStandardNodeMaterial | THREE.MeshToonNodeMaterial;
  coreMat: THREE.MeshBasicNodeMaterial; eyeMat: THREE.MeshBasicNodeMaterial;
}

export function buildHero(style: RenderStyle = 'standard'): HeroRig {
  const cel = style === 'cel';
  const mat = (c: number, e?: number, rough = 0.55) => cel ? toonMaterial({ color: c, emissive: e }) : new THREE.MeshStandardNodeMaterial({ color: c, roughness: rough, emissive: new THREE.Color(e ?? 0) });
  const skin = mat(0xffd2ac);
  const suit = mat(0x2a4fd0);         // deep cobalt bodysuit
  const suitDark = mat(0x1a2a6a);     // under-layer / gloves cuffs
  const plate = mat(0xe8ecf4);        // pale ceramic armour plates
  const trimMat = mat(0xff8a20, 0x401500);
  const mantleMat = mat(0x1c2350);    // midnight blue
  const brass = mat(0xd9a441, 0x2a1800);
  const copperMat = new THREE.MeshBasicNodeMaterial({ color: new THREE.Color(0xff9a50).multiplyScalar(1.6), fog: false });
  const dark = mat(0x22202c);
  const hairMat = mat(0x241c3a, 0x000000);
  const coreMat = new THREE.MeshBasicNodeMaterial({ color: new THREE.Color(0x7fe8ff).multiplyScalar(3), fog: false });
  const eyeMat = new THREE.MeshBasicNodeMaterial({ color: new THREE.Color(0x0e1630), fog: false });

  const root = new THREE.Group();
  const body = new THREE.Group(); root.add(body);
  const add = (parent: THREE.Object3D, g: THREE.BufferGeometry, m: THREE.Material, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, sx = 1, sy = 1, sz = 1) => {
    const mesh = new THREE.Mesh(g, m); mesh.position.set(x, y, z); mesh.rotation.set(rx, ry, rz); mesh.scale.set(sx, sy, sz); mesh.castShadow = true; parent.add(mesh); return mesh;
  };
  const grp = (parent: THREE.Object3D, x: number, y: number, z: number) => { const g = new THREE.Group(); g.position.set(x, y, z); parent.add(g); return g; };

  // pelvis + belt
  add(body, new THREE.SphereGeometry(0.27, 14, 10), suitDark, 0, 0.02, 0, 0, 0, 0, 1.1, 0.75, 0.85);
  add(body, new THREE.TorusGeometry(0.27, 0.055, 6, 18), trimMat, 0, 0.1, 0, Math.PI / 2);
  add(body, new THREE.BoxGeometry(0.16, 0.12, 0.06), plate, 0, 0.1, 0.26); // buckle

  // torso (counter-rotates against the hips while walking)
  const torso = grp(body, 0, 0.12, 0);
  add(torso, new THREE.CylinderGeometry(0.36, 0.25, 0.62, 12), suit, 0, 0.32, 0, 0, 0, 0, 1, 1, 0.78);
  add(torso, new THREE.BoxGeometry(0.5, 0.3, 0.12), plate, 0, 0.44, 0.2, -0.12);          // chest plate
  add(torso, new THREE.OctahedronGeometry(0.1, 0), coreMat, 0, 0.44, 0.28, 0, Math.PI / 4, 0, 0.9, 1.25, 0.6); // shard core
  add(torso, new THREE.TorusGeometry(0.15, 0.025, 4, 12), trimMat, 0, 0.44, 0.26);       // core housing
  add(torso, new THREE.CylinderGeometry(0.11, 0.13, 0.14, 8), skin, 0, 0.7, 0);          // neck
  // collar the scarf wraps around
  add(torso, new THREE.TorusGeometry(0.17, 0.06, 6, 14), trimMat, 0, 0.66, 0, Math.PI / 2);
  // shoulder guards: angular plates
  // right shoulder: ivory plate; left shoulder: mantle clasp (asymmetric silhouette)
  add(torso, new THREE.SphereGeometry(0.17, 10, 8, 0, Math.PI * 2, 0, Math.PI / 2), plate, -0.42, 0.6, 0, 0, 0, 0.35, 1.15, 0.9, 1.1);
  add(torso, new THREE.BoxGeometry(0.06, 0.04, 0.28), trimMat, -0.5, 0.58, 0, 0, 0, 0.6);
  add(torso, new THREE.SphereGeometry(0.2, 10, 8, 0, Math.PI * 2, 0, Math.PI / 2), mantleMat, 0.4, 0.6, -0.02, 0, 0, -0.45, 1.2, 0.85, 1.25);
  add(torso, new THREE.CylinderGeometry(0.05, 0.05, 0.04, 8), brass, 0.2, 0.62, 0.22, Math.PI / 2);

  // head
  const head = grp(torso, 0, 0.98, 0);
  add(head, new THREE.SphereGeometry(0.26, 18, 14), skin, 0, 0, 0, 0, 0, 0, 0.95, 1.05, 1);
  add(head, new THREE.SphereGeometry(0.13, 10, 8), skin, 0, -0.12, 0.08, 0, 0, 0, 1.1, 0.8, 1); // jaw
  for (const sgn of [1, -1]) {
    add(head, new THREE.SphereGeometry(0.05, 10, 8), eyeMat, sgn * 0.095, 0.0, 0.225, 0, 0, 0, 0.75, 1.25, 0.4);
    add(head, new THREE.BoxGeometry(0.11, 0.022, 0.02), dark, sgn * 0.1, 0.075, 0.235, 0, 0, sgn * -0.25); // brows
  }
  // hair: dark, cropped and swept — a low cap with short angular locks (no tall spikes)
  add(head, new THREE.SphereGeometry(0.275, 16, 10, 0, Math.PI * 2, 0, Math.PI * 0.55), hairMat, 0, 0.03, -0.015, -0.12, 0, 0, 1.02, 1.0, 1.05);
  const lock = new THREE.ConeGeometry(0.09, 0.3, 4);
  const locks: [number, number, number, number, number, number, number][] = [
    [0, 0.22, 0.12, 1.15, 0, 0.1, 0.9], [0.12, 0.2, 0.13, 1.1, 0, -0.45, 0.75], [-0.12, 0.19, 0.14, 1.2, 0, 0.5, 0.8],
    [0.05, 0.25, -0.05, -0.6, 0, -0.2, 0.9], [-0.07, 0.24, -0.08, -0.75, 0, 0.25, 0.85], [0, 0.16, -0.22, -1.45, 0, 0, 0.8],
    [0.2, 0.12, -0.12, -1.0, 0, -1.0, 0.7], [-0.21, 0.1, -0.1, -1.0, 0, 1.05, 0.7], [0.22, 0.02, 0.06, 0.2, 0, -1.6, 0.55],
  ];
  for (const [x, y, z, rx, ry, rz, l] of locks) add(head, lock, hairMat, x, y, z, rx, ry, rz, 1, l, 0.55);
  // brass side clasp (left temple)
  add(head, new THREE.BoxGeometry(0.05, 0.1, 0.07), brass, 0.25, 0.06, 0.06, 0, 0, -0.2);
  add(head, new THREE.TorusGeometry(0.035, 0.012, 4, 10), brass, 0.27, 0.06, 0.06, 0, Math.PI / 2, 0);

  // arms: shoulder pivot → elbow pivot
  const mkArm = (s: number) => {
    const sh = grp(torso, s * 0.44, 0.56, 0);
    add(sh, new THREE.CapsuleGeometry(0.095, 0.2, 4, 8), suit, 0, -0.17, 0);
    const fore = grp(sh, 0, -0.34, 0);
    add(fore, new THREE.SphereGeometry(0.09, 8, 6), suitDark);                         // elbow
    add(fore, new THREE.CapsuleGeometry(0.085, 0.17, 4, 8), suitDark, 0, -0.15, 0);
    add(fore, new THREE.CylinderGeometry(0.115, 0.1, 0.13, 8), plate, 0, -0.2, 0);      // gauntlet
    add(fore, new THREE.TorusGeometry(0.105, 0.022, 4, 10), trimMat, 0, -0.27, 0, Math.PI / 2);
    add(fore, new THREE.SphereGeometry(0.1, 10, 8), skin, 0, -0.35, 0.01, 0, 0, 0, 0.9, 1, 1.05); // fist
    return [sh, fore] as const;
  };
  // legs: hip pivot → knee pivot; soles end at y = -HERO_SOLE
  const mkLeg = (s: number) => {
    const hip = grp(body, s * 0.15, -0.08, 0);
    add(hip, new THREE.CapsuleGeometry(0.125, 0.26, 4, 8), suit, 0, -0.22, 0);
    const shin = grp(hip, 0, -0.46, 0);
    add(shin, new THREE.SphereGeometry(0.11, 8, 6), plate, 0, 0, 0.04, 0, 0, 0, 1, 1, 0.8);  // knee guard
    add(shin, new THREE.CapsuleGeometry(0.105, 0.2, 4, 8), suitDark, 0, -0.18, 0);
    add(shin, new THREE.CylinderGeometry(0.135, 0.15, 0.22, 8), plate, 0, -0.4, 0);        // boot shaft
    add(shin, new THREE.TorusGeometry(0.14, 0.025, 4, 10), trimMat, 0, -0.3, 0, Math.PI / 2); // cuff
    add(shin, new THREE.BoxGeometry(0.22, 0.12, 0.38), dark, 0, -0.52, 0.07);               // sole (bottom at -0.58)
    add(shin, new THREE.BoxGeometry(0.2, 0.06, 0.12), trimMat, 0, -0.47, 0.22);              // toe cap
    return [hip, shin] as const;
  };
  const [armL, foreL] = mkArm(1), [armR, foreR] = mkArm(-1);
  const [legL, shinL] = mkLeg(1), [legR, shinR] = mkLeg(-1);

  // split-tail scarf: two chains from the collar; the view swings them for follow-through
  const segGeo = new THREE.BoxGeometry(0.13, 0.26, 0.022); segGeo.translate(0, -0.13, 0);
  const knot = grp(torso, 0.05, 0.64, -0.18);
  add(knot, new THREE.SphereGeometry(0.07, 8, 6), trimMat);
  const scarf: THREE.Group[][] = [];
  for (const side of [1, -1]) {
    const tail: THREE.Group[] = [];
    let parent: THREE.Object3D = grp(knot, side * 0.06, 0, 0);
    for (let k = 0; k < 5; k++) {
      const seg = grp(parent, 0, k === 0 ? 0 : -0.25, 0);
      add(seg, segGeo, trimMat, 0, 0, 0, 0, 0, 0, 1 - k * 0.1, 1, 1);
      tail.push(seg); parent = seg;
    }
    tail[0].rotation.z = side * 0.18;
    scarf.push(tail);
  }
  // asymmetric mantle: hangs from the left shoulder down the back
  const mantle: THREE.Group[] = [];
  const mGeo = new THREE.BoxGeometry(0.42, 0.3, 0.03); mGeo.translate(0, -0.15, 0);
  let mp: THREE.Object3D = grp(torso, 0.24, 0.62, -0.2);
  for (let k = 0; k < 3; k++) {
    const seg = grp(mp, 0, k === 0 ? 0 : -0.29, 0);
    add(seg, mGeo, mantleMat, 0, 0, 0, 0, 0, 0, 1 - k * 0.12, 1, 1);
    mantle.push(seg); mp = seg;
  }
  mantle[0].rotation.z = 0.12;
  // offset floating copper ring behind the left shoulder: six arc segments (they separate in Surge Form)
  const ring = grp(torso, 0.34, 0.95, -0.42);
  const ringSegs: THREE.Mesh[] = [];
  const arc = new THREE.TorusGeometry(0.34, 0.03, 4, 8, (Math.PI * 2) / 6 - 0.12);
  for (let k = 0; k < 6; k++) {
    const m = add(ring, arc, copperMat, 0, 0, 0, 0, 0, (k / 6) * Math.PI * 2);
    m.castShadow = false; ringSegs.push(m);
  }

  root.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && (m.material === coreMat || m.material === eyeMat || m.material === copperMat)) m.userData.noOutline = true; });
  if (cel) addOutlines(root, 0.03);
  return { root, body, torso, head, armL, armR, foreL, foreR, legL, legR, shinL, shinR, scarf, mantle, ring, ringSegs, hairMat, trimMat, coreMat, eyeMat };
}
